import { useMemo } from "react"
import useTranslator from "@pma/hooks/use-translator"
import { formatCurrency } from "@pma/utils/utils"
import { cn } from "@pma/utils/helpers"
import { Card, CardContent } from "@components/common/card"
import { Progress } from "@components/common/progress"
import { DictType } from "@pma/types"
import { normalizeBoqItem, type NormalizedBoqItem } from "@pma/types/xano-models"

interface Props {
  boqItems: DictType[]
}

export default function ContractorBillingSummary({ boqItems }: Props) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const summary = useMemo(() => {
    if (!boqItems || boqItems.length === 0) {
      return { totalContract: 0, totalBilled: 0, totalPaid: 0, remaining: 0, completionPct: 0 }
    }

    const items: NormalizedBoqItem[] = boqItems.map(normalizeBoqItem)

    let totalContract = 0
    let totalPaid = 0

    items.forEach((item) => {
      totalContract += item.contractTotal
      totalPaid += item.paidTotal
    })

    const remaining = totalContract - totalPaid
    const completionPct = totalContract > 0 ? (totalPaid / totalContract) * 100 : 0

    return {
      totalContract,
      totalBilled: totalPaid,
      totalPaid,
      remaining,
      completionPct,
    }
  }, [boqItems])

  if (!boqItems || boqItems.length === 0) return null

  const kpis = [
    { label: translate("total_contract"), value: summary.totalContract, color: "text-foreground" },
    { label: translate("total_billed"), value: summary.totalBilled, color: "text-blue-600" },
    { label: translate("total_paid"), value: summary.totalPaid, color: "text-green-600" },
    { label: translate("remaining_amount"), value: summary.remaining, color: "text-orange-600" },
  ]

  return (
    <Card
      className={cn("mb-4 border-slate-200")}
      dir={isHebrew ? "rtl" : "ltr"}
    >
      <CardContent className="pt-4 pb-4 px-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="flex flex-col items-center rounded-lg bg-muted/30 p-3">
              <span className="text-xs text-muted-foreground">{kpi.label}</span>
              <span className={cn("text-sm font-bold tabular-nums sm:text-base", kpi.color)}>
                {formatCurrency(kpi.value, isHebrew)}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-3 space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{translate("billing_progress")}</span>
            <span className="font-medium">{summary.completionPct.toFixed(1)}%</span>
          </div>
          <Progress value={Math.min(summary.completionPct, 100)} className="h-2" />
        </div>
      </CardContent>
    </Card>
  )
}
